/* RFQ Comparison Workspace — unified procurement decision screen.
 *
 * One canvas, four panels:
 *   1. RFQ header + supplier ranking strip (vendor score + total + lead time)
 *   2. 3-layer Comparison Matrix (commercial + intelligence + decision cells)
 *   3. Per-line recommendation chips (accept / negotiate / reject)
 *   4. Award workspace — per-line supplier picker + Award action
 *
 * Backend wired:
 *   GET /api/rfqs                         — list
 *   GET /api/rfqs/{id}                    — header + quotations + awards
 *   GET /api/rfqs/{id}/comparison         — 3-layer matrix
 *   GET /api/rfqs/{id}/recommendation     — procurement engine output
 *   POST /api/rfqs/{id}/awards            — partial award
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Package, TrendingUp, TrendingDown, Award, CheckCircle2,
  AlertTriangle, XCircle, ShieldCheck, Clock, ArrowRight,
  Lock, FileText, Truck,
} from 'lucide-react';


// ─── helpers ───────────────────────────────────────────────────────


const varianceTone = (pct) => {
  if (pct == null) return 'text-gray-500';
  if (pct <= 5)  return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (pct <= 20) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-rose-700 bg-rose-50 border-rose-200';
};

const recTone = {
  accept:    'bg-emerald-100 border-emerald-400 text-emerald-900',
  negotiate: 'bg-amber-100 border-amber-400 text-amber-900',
  reject:    'bg-rose-100 border-rose-400 text-rose-900',
  no_quotes: 'bg-gray-100 border-gray-300 text-gray-700',
};

const recIcon = {
  accept:    CheckCircle2,
  negotiate: AlertTriangle,
  reject:    XCircle,
  no_quotes: AlertTriangle,
};


// ─── List view ─────────────────────────────────────────────────────


export const RFQList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    title: '', project_id: '', deadline_date: '', notes: '',
    items: [{ description: '', quantity: 1, uom: 'ea' }],
  });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/rfqs?limit=200');
      setRows(r.data?.rfqs || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    axiosInstance.get('/projects?limit=200').then(r => {
      setProjects(r.data?.projects || []);
    }).catch(() => setProjects([]));
  }, [load]);

  const updateItem = (idx, key, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      return { ...f, items };
    });
  };
  const addItem  = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, uom: 'ea' }] }));
  const dropItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const submitRfq = async () => {
    const items = (form.items || [])
      .filter(it => (it.description || '').trim() && Number(it.quantity || 0) > 0);
    if (items.length === 0) {
      toast.error('At least one item with description + quantity is required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        title:    form.title || `RFQ ${new Date().toISOString().slice(0, 10)}`,
        project_id: form.project_id || null,
        deadline_date: form.deadline_date || null,
        notes: form.notes || null,
        items,
        // Intentionally NO invited_suppliers — auto-discovery will surface them.
      };
      const r = await axiosInstance.post('/rfqs', payload);
      const newId = r.data?.id;
      toast.success(`${r.data?.rfq_no} created — auto-discovering suppliers`);
      setShowCreate(false);
      setForm({
        title: '', project_id: '', deadline_date: '', notes: '',
        items: [{ description: '', quantity: 1, uom: 'ea' }],
      });
      if (newId) navigate(`/rfqs/${newId}`);
      else load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'Create failed');
    } finally { setCreating(false); }
  };

  const statusTone = {
    draft:      'bg-gray-100 text-gray-700',
    sent:       'bg-blue-100 text-blue-800',
    quoted:     'bg-violet-100 text-violet-800',
    awarded:    'bg-emerald-100 text-emerald-800',
    po_created: 'bg-indigo-100 text-indigo-800',
    closed:     'bg-gray-100 text-gray-500',
    cancelled:  'bg-rose-100 text-rose-700',
  };

  return (
    <div className="p-6 space-y-4" data-testid="rfq-list-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-6 h-6" />
          Request for Quotations
          <span className="text-sm font-mono px-2 py-0.5 rounded bg-gray-900 text-white"
                data-testid="rfq-count">{rows.length}</span>
        </h1>
        <button onClick={() => setShowCreate(true)}
                data-testid="rfq-new-button"
                className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1">
          + New RFQ
        </button>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">RFQ #</th>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Items</th>
              <th className="text-right px-3 py-2">Suppliers</th>
              <th className="text-left px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8" data-testid="rfq-list-empty">
                No RFQs yet.
              </td></tr>
            )}
            {rows.map((rfq) => (
              <tr key={rfq.id}
                  onClick={() => navigate(`/rfqs/${rfq.id}`)}
                  data-testid={`rfq-row-${rfq.rfq_no}`}
                  className="cursor-pointer border-t border-gray-100 hover:bg-indigo-50/30">
                <td className="px-3 py-2 font-mono text-xs">{rfq.rfq_no}</td>
                <td className="px-3 py-2">{rfq.title}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${statusTone[rfq.status] || 'bg-gray-100'}`}>
                    {rfq.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono">{(rfq.items || []).length}</td>
                <td className="px-3 py-2 text-right font-mono">{(rfq.invited_suppliers || []).length}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{rfq.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New RFQ modal — items + project + due date + notes only */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             data-testid="rfq-create-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Package className="w-5 h-5" /> New RFQ
                <span className="text-xs font-normal text-gray-500">
                  (suppliers auto-discovered after save)
                </span>
              </h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700">Title</label>
                  <input type="text" value={form.title}
                          onChange={(e) => setForm(f => ({...f, title: e.target.value}))}
                          placeholder="e.g. Cement & Aggregates Q2"
                          data-testid="rfq-form-title"
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Project (optional)</label>
                  <select value={form.project_id}
                           onChange={(e) => setForm(f => ({...f, project_id: e.target.value}))}
                           data-testid="rfq-form-project"
                           className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                    <option value="">— none —</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.project_code || p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Required by date</label>
                  <input type="date" value={form.deadline_date}
                          onChange={(e) => setForm(f => ({...f, deadline_date: e.target.value}))}
                          data-testid="rfq-form-deadline"
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Notes</label>
                <textarea rows={2} value={form.notes}
                           onChange={(e) => setForm(f => ({...f, notes: e.target.value}))}
                           placeholder="Anything the system should remember about this RFQ…"
                           data-testid="rfq-form-notes"
                           className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-y" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">Items</label>
                  <button onClick={addItem} data-testid="rfq-form-add-item"
                          className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 font-semibold">
                    + Add item
                  </button>
                </div>
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead className="bg-gray-50 text-xs">
                    <tr>
                      <th className="text-left px-2 py-1">Description</th>
                      <th className="text-right px-2 py-1 w-24">Quantity</th>
                      <th className="text-left px-2 py-1 w-20">Unit</th>
                      <th className="px-1 py-1 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-gray-100"
                          data-testid={`rfq-form-item-row-${idx}`}>
                        <td className="px-2 py-1">
                          <input type="text" value={it.description}
                                  onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                  placeholder="e.g. Cable 10mm² red"
                                  data-testid={`rfq-form-item-desc-${idx}`}
                                  className="w-full border border-gray-200 rounded px-2 py-1 text-xs" />
                        </td>
                        <td className="px-2 py-1">
                          <input type="number" min={0} value={it.quantity}
                                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                  data-testid={`rfq-form-item-qty-${idx}`}
                                  className="w-full text-right border border-gray-200 rounded px-2 py-1 text-xs" />
                        </td>
                        <td className="px-2 py-1">
                          <input type="text" value={it.uom}
                                  onChange={(e) => updateItem(idx, 'uom', e.target.value)}
                                  placeholder="ea"
                                  data-testid={`rfq-form-item-uom-${idx}`}
                                  className="w-full border border-gray-200 rounded px-2 py-1 text-xs" />
                        </td>
                        <td className="px-1 py-1">
                          {form.items.length > 1 && (
                            <button onClick={() => dropItem(idx)}
                                    data-testid={`rfq-form-item-drop-${idx}`}
                                    className="text-rose-500 hover:text-rose-700 text-lg">×</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-[11px] text-gray-500">
                  💡 No need to pick suppliers — after save, the system scans your Vendor
                  Quotations and surfaces eligible suppliers with coverage %.
                </p>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)}
                      data-testid="rfq-form-cancel"
                      className="px-3 py-1.5 rounded border border-gray-300 text-sm">Cancel</button>
              <button onClick={submitRfq} disabled={creating}
                      data-testid="rfq-form-submit"
                      className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50">
                {creating ? 'Creating…' : 'Create RFQ →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Workspace (single RFQ unified screen) ─────────────────────────


export const RFQWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [header, setHeader]       = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [awards, setAwards]       = useState([]);
  const [matrix, setMatrix]       = useState(null);
  const [rec, setRec]             = useState(null);
  const [awardPicks, setAwardPicks] = useState({});  // rfq_item_id → {supplier_id, quotation_id}
  const [spawnedPOs, setSpawnedPOs] = useState([]);
  const [vendorQuoteCmp, setVendorQuoteCmp] = useState(null);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [awardChoices, setAwardChoices] = useState({});  // rfq_item_id → supplier_id
  const [busy, setBusy]           = useState(false);
  // New flow — auto-discovered suppliers + filter mode + selection
  const [eligible, setEligible]   = useState(null);
  const [pickedSupplierIds, setPickedSupplierIds] = useState([]);
  const [filterMode, setFilterMode] = useState('latest_only');

  const refresh = useCallback(async () => {
    try {
      const [g, m, r, posResp, vqc, el] = await Promise.all([
        axiosInstance.get(`/rfqs/${id}`),
        axiosInstance.get(`/rfqs/${id}/comparison`).catch(() => null),
        axiosInstance.get(`/rfqs/${id}/recommendation`).catch(() => null),
        axiosInstance.get(`/rfqs/${id}/purchase-orders`).catch(() => null),
        axiosInstance.get(`/rfqs/${id}/vendor-quote-comparison?filter_mode=${filterMode}`).catch(() => null),
        axiosInstance.get(`/rfqs/${id}/eligible-suppliers`).catch(() => null),
      ]);
      setHeader(g.data?.rfq || null);
      setQuotations(g.data?.supplier_quotations || []);
      setAwards(g.data?.awards || []);
      setMatrix(m?.data || null);
      setRec(r?.data || null);
      setSpawnedPOs(posResp?.data?.purchase_orders || []);
      setVendorQuoteCmp(vqc?.data || null);
      setEligible(el?.data || null);
      // Pre-select suppliers already invited.
      if (el?.data?.current_invited_suppliers) {
        setPickedSupplierIds(el.data.current_invited_suppliers);
      }
      // Default each line's pick to the engine's cheapest cell (when no award exists yet).
      if (m?.data?.items) {
        const picks = {};
        m.data.items.forEach((row) => {
          const cheapest = (row.cells || []).find((c) => c.is_cheapest);
          if (cheapest) picks[row.rfq_item_id] = {
            supplier_id: cheapest.supplier_id,
            quotation_id: cheapest.quotation_id,
            unit_price: cheapest.unit_price,
          };
        });
        setAwardPicks(picks);
      }
    } catch (_) {
      toast.error('Failed to load RFQ');
    }
  }, [id, filterMode]);

  useEffect(() => { refresh(); }, [refresh]);

  const selectSuppliersAndCompare = async () => {
    if (pickedSupplierIds.length === 0) {
      toast.error('Pick at least one supplier');
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post(`/rfqs/${id}/select-suppliers`, {
        supplier_ids: pickedSupplierIds,
      });
      toast.success(`${pickedSupplierIds.length} supplier(s) selected — building comparison`);
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'Selection failed');
    } finally { setBusy(false); }
  };

  const sendRFQ = async () => {
    setBusy(true);
    try {
      await axiosInstance.post(`/rfqs/${id}/send`);
      toast.success('RFQ sent to invited suppliers');
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'Send failed');
    } finally { setBusy(false); }
  };

  const submitAwards = async () => {
    if (!matrix) return;
    setBusy(true);
    try {
      const lines = matrix.items.map((row) => {
        const pick = awardPicks[row.rfq_item_id];
        if (!pick) return null;
        return {
          rfq_item_id:   row.rfq_item_id,
          supplier_id:   pick.supplier_id,
          quotation_id:  pick.quotation_id,
          awarded_quantity: row.quantity,
          unit_price:    pick.unit_price,
          reason:        'workspace award',
        };
      }).filter(Boolean);
      if (lines.length === 0) {
        toast.error('Pick a supplier for at least one line');
        return;
      }
      const r = await axiosInstance.post(`/rfqs/${id}/awards`, { lines });
      toast.success(`${r.data?.awarded_count || 0} line(s) awarded`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'Award failed');
    } finally { setBusy(false); }
  };

  const lockRecommendation = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/rfqs/${id}/lock-recommendation`);
      toast.success(`Recommendation locked — hash ${r.data?.hash?.slice(0,10)}…`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'Lock failed');
    } finally { setBusy(false); }
  };

  const generatePOs = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/rfqs/${id}/generate-pos`, { vat_rate: 15.0 });
      toast.success(`${r.data?.po_count || 0} purchase order(s) generated`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'PO generation failed');
    } finally { setBusy(false); }
  };

  if (!header) {
    return <div className="p-6 text-sm text-gray-500" data-testid="rfq-workspace-loading">Loading workspace…</div>;
  }

  const lineRecByItem = Object.fromEntries(
    (rec?.lines || []).map((l) => [l.rfq_item_id, l])
  );
  const isAwarded = header.status === 'awarded' || awards.length > 0;

  return (
    <div className="p-6 space-y-4" data-testid="rfq-workspace">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <button onClick={() => navigate('/rfqs')}
                  className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-1">
            ← Back to RFQs
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {header.rfq_no}
            <span className="text-base font-normal text-gray-600">— {header.title}</span>
            <span className="text-xs px-2 py-0.5 rounded bg-violet-100 text-violet-800 font-semibold uppercase"
                  data-testid="rfq-workspace-status">
              {header.status}
            </span>
          </h1>
        </div>
        <div className="flex gap-2">
          {header.status === 'draft' && (
            <button onClick={sendRFQ} disabled={busy}
                    data-testid="rfq-send-button"
                    className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <ArrowRight className="w-4 h-4" /> Send to Suppliers
            </button>
          )}
        </div>
      </div>

      {/* ─── NEW FLOW: Auto-Discovered Suppliers + Coverage % + Selection ─── */}
      {eligible && (eligible.suppliers?.length > 0 || eligible.current_invited_suppliers?.length > 0) && (
        <div className="border border-sky-300 bg-white rounded-lg overflow-hidden"
             data-testid="rfq-supplier-discovery">
          <div className="px-3 py-2 bg-sky-50 border-b border-sky-300 font-semibold text-sm text-sky-900 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Auto-Discovered Suppliers
              <span className="text-xs font-normal text-sky-700">
                ({eligible.suppliers?.length || 0} found · covers {eligible.total_items} item{eligible.total_items === 1 ? '' : 's'})
              </span>
            </span>
            <button
              onClick={selectSuppliersAndCompare}
              disabled={busy || pickedSupplierIds.length === 0}
              data-testid="rfq-select-suppliers-btn"
              className="px-3 py-1 rounded bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold disabled:opacity-50">
              ✓ Use Selected ({pickedSupplierIds.length}) for Comparison
            </button>
          </div>
          {(eligible.suppliers || []).length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500" data-testid="rfq-no-eligible">
              No suppliers have an active Vendor Quotation covering these items.
              <button onClick={() => navigate('/vendor-quotations')}
                      className="ml-2 text-sky-700 hover:underline font-semibold">
                + Capture a Vendor Quotation
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Supplier</th>
                  <th className="text-left px-3 py-2">Coverage</th>
                  <th className="text-left px-3 py-2">Last Quote</th>
                  <th className="text-left px-3 py-2">Quote Date</th>
                  <th className="text-left px-3 py-2">Payment Terms</th>
                  <th className="text-right px-3 py-2">Avg Lead</th>
                </tr>
              </thead>
              <tbody>
                {eligible.suppliers.map((s) => (
                  <tr key={s.supplier_id} className="border-t border-gray-100"
                      data-testid={`discovery-supplier-row-${s.supplier_id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox"
                              checked={pickedSupplierIds.includes(s.supplier_id)}
                              onChange={(e) => {
                                setPickedSupplierIds(prev =>
                                  e.target.checked
                                    ? [...prev, s.supplier_id]
                                    : prev.filter(x => x !== s.supplier_id));
                              }}
                              data-testid={`discovery-checkbox-${s.supplier_id}`} />
                    </td>
                    <td className="px-3 py-2 font-medium">{s.supplier_name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        s.coverage_pct === 100 ? 'bg-emerald-100 text-emerald-800' :
                        s.coverage_pct >= 50  ? 'bg-amber-100 text-amber-800'   :
                                                'bg-rose-100  text-rose-700'
                      }`}
                      data-testid={`discovery-coverage-${s.supplier_id}`}>
                        {s.coverage_pct}% ({s.covered_items}/{s.total_items})
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {s.last_quote_no ? (
                        <a href={`/vendor-quotations/${s.last_quote_id}`}
                            className="text-indigo-700 hover:underline">{s.last_quote_no}</a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{s.last_quote_date || '—'}</td>
                    <td className="px-3 py-2 text-xs">{s.payment_terms || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {s.avg_lead_time_days != null ? `${s.avg_lead_time_days}d` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ─── NEW FLOW: Supplier Quote Selector Filters ─── */}
      {vendorQuoteCmp?.working_supplier_ids?.length > 0 && (
        <div className="border border-violet-300 bg-violet-50/40 rounded-lg p-3 flex items-center gap-3 flex-wrap"
             data-testid="rfq-quote-selector-filters">
          <span className="text-xs font-semibold text-violet-900 uppercase tracking-wider">
            Quote Selector
          </span>
          <select value={filterMode}
                   onChange={(e) => setFilterMode(e.target.value)}
                   data-testid="rfq-filter-mode-select"
                   className="text-xs border border-violet-300 bg-white rounded px-2 py-1">
            <option value="latest_only">Latest Quotations Only</option>
            <option value="all_active">All Active Quotations</option>
            <option value="lowest_price">Lowest Price Per Supplier</option>
            <option value="valid_today">Valid Until Today</option>
            <option value="last_90_days">Within Last 90 Days</option>
          </select>
          {/* Best Commercial / Delivery / Overall chips */}
          {vendorQuoteCmp.best_commercial_supplier_id && (
            <span data-testid="rfq-best-commercial-chip"
                  className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-900 font-semibold inline-flex items-center gap-1">
              💰 Best Commercial:{' '}
              {vendorQuoteCmp.invited_suppliers.find(x => x.supplier_id === vendorQuoteCmp.best_commercial_supplier_id)?.supplier_name}
            </span>
          )}
          {vendorQuoteCmp.best_delivery_supplier_id && (
            <span data-testid="rfq-best-delivery-chip"
                  className="text-xs px-2 py-1 rounded-full bg-violet-100 text-violet-900 font-semibold inline-flex items-center gap-1">
              🚚 Best Delivery:{' '}
              {vendorQuoteCmp.invited_suppliers.find(x => x.supplier_id === vendorQuoteCmp.best_delivery_supplier_id)?.supplier_name}
            </span>
          )}
          {vendorQuoteCmp.best_overall_supplier_id && (
            <span data-testid="rfq-best-overall-chip"
                  className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900 font-semibold inline-flex items-center gap-1">
              ⭐ Best Overall:{' '}
              {vendorQuoteCmp.invited_suppliers.find(x => x.supplier_id === vendorQuoteCmp.best_overall_supplier_id)?.supplier_name}
            </span>
          )}
        </div>
      )}

      {/* Supplier Ranking Strip (legacy supplier_quotations path) */}
      {quotations.length > 0 && matrix?.suppliers?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2"
             data-testid="rfq-supplier-ranking">
          {matrix.suppliers.map((s) => {
            const isRecommended = matrix.recommendation?.supplier_id === s.supplier_id;
            return (
              <div key={s.supplier_id}
                   data-testid={`supplier-card-${s.supplier_id}`}
                   className={`border rounded-lg p-3 ${isRecommended ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-sm flex items-center gap-1">
                    {s.supplier_name}
                    {isRecommended && <ShieldCheck className="w-4 h-4 text-emerald-600" />}
                  </div>
                  {s.vendor_score != null && (
                    <span data-testid={`supplier-vendor-score-${s.supplier_id}`}
                          className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100">
                      score {s.vendor_score}
                    </span>
                  )}
                </div>
                <div className="text-xs space-y-0.5 text-gray-700">
                  <div className="flex justify-between"><span>Total</span><span className="font-mono font-semibold">{s.total?.toLocaleString()}</span></div>
                  <div className="flex justify-between">
                    <span>Lead time</span>
                    <span className="font-mono">{s.lead_time_days != null ? `${s.lead_time_days} d` : '—'}</span>
                  </div>
                  {s.on_time_delivery_pct != null && (
                    <div className="flex justify-between"><span>On-time</span><span className="font-mono">{s.on_time_delivery_pct}%</span></div>
                  )}
                  {s.revision_number > 0 && (
                    <div className="text-[10px] text-violet-700 font-mono">V{s.revision_number}</div>
                  )}
                </div>
                {isRecommended && (matrix.recommendation?.reasons || []).length > 0 && (
                  <ul className="mt-2 text-[11px] text-emerald-800 space-y-0.5">
                    {matrix.recommendation.reasons.map((r, i) => (
                      <li key={i}>· {r}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Comparison Matrix (legacy supplier_quotations path) */}
      {quotations.length > 0 && matrix?.items?.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white"
             data-testid="rfq-comparison-matrix">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-sm">
            Comparison Matrix
            <span className="ml-2 text-xs text-gray-500 font-normal">
              {matrix.items.length} item(s) × {matrix.suppliers.length} supplier(s)
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 min-w-[200px]">Item</th>
                  {matrix.suppliers.map((s) => (
                    <th key={s.supplier_id} className="text-left px-3 py-2 min-w-[180px]">
                      {s.supplier_name}
                    </th>
                  ))}
                  <th className="text-left px-3 py-2 min-w-[140px]">Recommendation</th>
                  <th className="text-left px-3 py-2 min-w-[180px]">Award To</th>
                </tr>
              </thead>
              <tbody>
                {matrix.items.map((row) => {
                  const lineRec = lineRecByItem[row.rfq_item_id];
                  const Icon = recIcon[lineRec?.recommendation || 'no_quotes'] || AlertTriangle;
                  return (
                    <tr key={row.rfq_item_id} className="border-t border-gray-100 align-top"
                        data-testid={`matrix-row-${row.rfq_item_id}`}>
                      <td className="px-3 py-2 sticky left-0 bg-white">
                        <div className="font-medium">{row.description}</div>
                        <div className="text-[11px] text-gray-500 font-mono">
                          {row.item_code || '—'} · {row.quantity} {row.uom}
                        </div>
                        {row.catalog?.average_purchase_price && (
                          <div className="text-[11px] text-violet-600 mt-0.5">
                            avg {row.catalog.average_purchase_price}{row.catalog.last_purchase_price && ` · last ${row.catalog.last_purchase_price}`}
                          </div>
                        )}
                      </td>
                      {matrix.suppliers.map((s) => {
                        const cell = row.cells.find((c) => c.supplier_id === s.supplier_id);
                        if (!cell) return <td key={s.supplier_id} className="px-3 py-2 text-gray-400">—</td>;
                        return (
                          <td key={s.supplier_id} className="px-3 py-2"
                              data-testid={`matrix-cell-${row.rfq_item_id}-${s.supplier_id}`}>
                            <div className={`font-mono text-sm ${cell.is_cheapest ? 'text-emerald-700 font-bold' : ''}`}>
                              {cell.unit_price.toLocaleString()}
                              {cell.is_cheapest && <span className="ml-1 text-[10px]">★</span>}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              total {cell.total.toLocaleString()}
                              {cell.lead_time_days != null && <> · {cell.lead_time_days}d</>}
                            </div>
                            {cell.variance_vs_avg_pct != null && (
                              <span data-testid={`variance-${row.rfq_item_id}-${s.supplier_id}`}
                                    className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded border ${varianceTone(cell.variance_vs_avg_pct)}`}>
                                vs avg {cell.variance_vs_avg_pct >= 0 ? '+' : ''}{cell.variance_vs_avg_pct}%
                              </span>
                            )}
                            {cell.variance_vs_last_pct != null && (
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                vs last {cell.variance_vs_last_pct >= 0 ? '+' : ''}{cell.variance_vs_last_pct}%
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2">
                        {lineRec && (
                          <div
                            data-testid={`line-rec-${row.rfq_item_id}`}
                            className={`text-xs px-2 py-1 rounded border ${recTone[lineRec.recommendation]}`}>
                            <div className="flex items-center gap-1 font-semibold uppercase">
                              <Icon className="w-3 h-3" />
                              {lineRec.recommendation}
                            </div>
                            {lineRec.reasons?.[0] && (
                              <div className="mt-1 text-[10px] opacity-80">{lineRec.reasons[0]}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          data-testid={`award-pick-${row.rfq_item_id}`}
                          value={awardPicks[row.rfq_item_id]?.supplier_id || ''}
                          disabled={isAwarded}
                          onChange={(e) => {
                            const sid = e.target.value;
                            const cell = row.cells.find((c) => c.supplier_id === sid);
                            setAwardPicks({
                              ...awardPicks,
                              [row.rfq_item_id]: cell ? {
                                supplier_id: cell.supplier_id,
                                quotation_id: cell.quotation_id,
                                unit_price: cell.unit_price,
                              } : null,
                            });
                          }}
                          className="text-xs border border-gray-300 rounded px-2 py-1 w-full">
                          <option value="">— pick —</option>
                          {row.cells.map((c) => (
                            <option key={c.supplier_id} value={c.supplier_id}>
                              {c.supplier_name} @ {c.unit_price}{c.is_cheapest ? ' ★' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Decision Panel (legacy supplier_quotations path) */}
      {quotations.length > 0 && matrix?.items?.length > 0 && (
        <div className="border border-indigo-300 bg-indigo-50 rounded-lg p-3 flex items-center justify-between gap-2"
             data-testid="rfq-decision-panel">
          <div className="text-sm">
            {rec?.overall_recommendation && (
              <span className="font-semibold">
                Overall recommendation:{' '}
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${recTone[rec.overall_recommendation]}`}
                      data-testid="rfq-overall-rec">
                  {rec.overall_recommendation.toUpperCase()}
                </span>
              </span>
            )}
            {rec?.summary && (
              <span className="ml-3 text-xs text-gray-600">
                accept {rec.summary.accept} · negotiate {rec.summary.negotiate} · reject {rec.summary.reject}
              </span>
            )}
          </div>
          {!isAwarded && (
            <button onClick={submitAwards} disabled={busy || header.status === 'draft'}
                    data-testid="rfq-award-button"
                    className="px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <Award className="w-4 h-4" /> Award Selected Lines
            </button>
          )}
          {isAwarded && (
            <span className="text-xs text-emerald-800 font-semibold inline-flex items-center gap-1"
                  data-testid="rfq-awarded-chip">
              <ShieldCheck className="w-4 h-4" />
              Awarded ({awards.length} line{awards.length === 1 ? '' : 's'})
            </span>
          )}
        </div>
      )}

      {/* User's 5-section flow: Invited Suppliers → Selection → Comparison → Analysis → Award Decision */}
      {vendorQuoteCmp?.supplier_quote_summary?.length > 0 && (
        <div className="space-y-3" data-testid="rfq-vendor-quote-comparison">

          {/* Section 1+2: Invited Suppliers with checkboxes */}
          <div className="border border-emerald-300 bg-white rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-300 font-semibold text-sm text-emerald-900 inline-flex items-center justify-between w-full">
              <span className="inline-flex items-center gap-2">
                <Truck className="w-4 h-4" /> 1. Invited Suppliers — Select quotes to compare
              </span>
              <button
                onClick={async () => {
                  if (selectedQuoteIds.length === 0) {
                    toast.error('Pick at least one quote');
                    return;
                  }
                  try {
                    const r = await axiosInstance.get(
                      `/rfqs/${id}/vendor-quote-comparison?selected_quote_ids=${selectedQuoteIds.join(',')}`
                    );
                    setVendorQuoteCmp(r.data);
                    setShowComparison(true);
                  } catch (_) { toast.error('Failed to build comparison'); }
                }}
                disabled={selectedQuoteIds.length === 0}
                data-testid="rfq-generate-comparison-btn"
                className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50">
                ⚙ Generate Comparison ({selectedQuoteIds.length})
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="text-left px-3 py-2">Supplier</th>
                  <th className="text-left px-3 py-2">Latest Quote</th>
                  <th className="text-left px-3 py-2">Quote Date</th>
                  <th className="text-left px-3 py-2">Valid Until</th>
                  <th className="text-left px-3 py-2">Payment Terms</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {vendorQuoteCmp.supplier_quote_summary.map((s) => (
                  <tr key={s.supplier_id} className="border-t border-gray-100"
                      data-testid={`rfq-vqc-supplier-row-${s.supplier_id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox"
                              disabled={!s.covered || s.status === 'expired'}
                              checked={s.quote_id ? selectedQuoteIds.includes(s.quote_id) : false}
                              onChange={(e) => {
                                if (!s.quote_id) return;
                                setSelectedQuoteIds(prev =>
                                  e.target.checked
                                    ? [...prev, s.quote_id]
                                    : prev.filter(x => x !== s.quote_id));
                              }}
                              data-testid={`rfq-vqc-checkbox-${s.supplier_id}`} />
                    </td>
                    <td className="px-3 py-2 inline-flex items-center gap-1">
                      <Truck className="w-3 h-3 text-gray-400" /> {s.supplier_name}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {s.quote_no ? (
                        <a href={`/vendor-quotations/${s.quote_id}`}
                            className="text-indigo-700 hover:underline">{s.quote_no}</a>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{s.quote_date || '—'}</td>
                    <td className="px-3 py-2 text-xs">{s.valid_until || '—'}</td>
                    <td className="px-3 py-2 text-xs">{s.payment_terms || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                        s.status === 'valid' ? 'bg-emerald-100 text-emerald-800' :
                        s.status === 'expired' ? 'bg-rose-100 text-rose-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {s.status === 'valid' ? '✓ Valid'
                          : s.status === 'expired' ? '⏰ Expired'
                          : '○ No quote'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sections 3+4+5 appear once the user generated the comparison */}
          {showComparison && vendorQuoteCmp.working_supplier_ids?.length > 0 && (
            <>
              {/* Section 3: Comparison Matrix */}
              <div className="border border-indigo-300 bg-white rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-300 font-semibold text-sm text-indigo-900 inline-flex items-center gap-2">
                  <Package className="w-4 h-4" /> 2. Comparison Matrix
                </div>
                <div className="overflow-auto">
                  <table className="w-full text-sm" data-testid="rfq-vqc-table">
                    <thead className="bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-3 py-2 sticky left-0 bg-gray-50">Item</th>
                        {vendorQuoteCmp.working_supplier_ids.map((sid) => {
                          const sup = vendorQuoteCmp.invited_suppliers.find(x => x.supplier_id === sid);
                          return <th key={sid} className="text-left px-3 py-2 min-w-[170px]">{sup?.supplier_name}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {vendorQuoteCmp.items.map((row) => (
                        <tr key={row.rfq_item_id || row.item_code} className="border-t border-gray-100"
                            data-testid={`rfq-vqc-row-${row.item_code || row.rfq_item_id}`}>
                          <td className="px-3 py-2 sticky left-0 bg-white border-r border-gray-100 align-top">
                            <div className="text-sm">{row.description}</div>
                            {row.item_code && <div className="text-[10px] font-mono text-gray-500">{row.item_code}</div>}
                            <div className="text-[10px] text-gray-500 mt-0.5">Qty: <span className="font-mono">{row.quantity}</span> {row.uom}</div>
                          </td>
                          {row.cells.map((c) => (
                            <td key={c.supplier_id}
                                className={`px-3 py-2 align-top text-xs ${c.is_cheapest ? 'bg-emerald-50' : ''} ${!c.covered ? 'text-gray-400' : ''}`}>
                              {!c.covered ? <span className="italic">— no quote —</span> : (
                                <>
                                  <div className="font-mono font-semibold text-base text-indigo-700 inline-flex items-center gap-1">
                                    {Number(c.unit_price).toLocaleString()} {c.currency}
                                    {c.is_cheapest && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-emerald-600 text-white font-bold">★ BEST</span>}
                                    {c.diff_pct_vs_cheapest > 0 && (
                                      <span className="text-[10px] text-rose-700 ml-1">+{c.diff_pct_vs_cheapest}%</span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-gray-700 mt-1">🚚 Delivery: <strong>{c.lead_time_days ?? '—'}{c.lead_time_days != null ? 'd' : ''}</strong>
                                    {c.is_fastest && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-600 text-white font-bold">FASTEST</span>}
                                  </div>
                                  {c.payment_terms && <div className="text-[11px] text-gray-700">💳 {c.payment_terms}
                                    {c.is_best_terms && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-600 text-white font-bold">BEST TERMS</span>}
                                  </div>}
                                  {c.valid_until && <div className="text-[10px] text-gray-500">⏰ Until {c.valid_until}</div>}
                                </>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 4: Per-item Analysis */}
              <div className="border border-amber-300 bg-amber-50/40 rounded-lg overflow-hidden"
                   data-testid="rfq-vqc-analysis">
                <div className="px-3 py-2 bg-amber-100 border-b border-amber-300 font-semibold text-sm text-amber-900 inline-flex items-center gap-2">
                  💡 3. Per-Item Analysis (recommendations only)
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-amber-50 text-xs text-amber-900">
                    <tr>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-left px-3 py-2">💰 Cheapest</th>
                      <th className="text-left px-3 py-2">🚚 Fastest</th>
                      <th className="text-left px-3 py-2">💳 Best Payment Terms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorQuoteCmp.items.map(row => {
                      const sname = sid => vendorQuoteCmp.invited_suppliers.find(s => s.supplier_id === sid)?.supplier_name || '—';
                      const a = row.analysis || {};
                      return (
                        <tr key={row.item_code || row.rfq_item_id} className="border-t border-amber-200">
                          <td className="px-3 py-2 text-sm">{row.description}</td>
                          <td className="px-3 py-2 text-xs"><strong>{sname(a.cheapest_supplier_id)}</strong> · <span className="font-mono">{a.cheapest_price}</span></td>
                          <td className="px-3 py-2 text-xs"><strong>{sname(a.fastest_supplier_id)}</strong> · <span className="font-mono">{a.fastest_lead_days != null ? `${a.fastest_lead_days}d` : '—'}</span></td>
                          <td className="px-3 py-2 text-xs"><strong>{sname(a.best_terms_supplier_id)}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Section 5: Award Decision — human decides */}
              <div className="border border-violet-300 bg-white rounded-lg overflow-hidden"
                   data-testid="rfq-vqc-award-decision">
                <div className="px-3 py-2 bg-violet-50 border-b border-violet-300 font-semibold text-sm text-violet-900 inline-flex items-center justify-between w-full">
                  <span className="inline-flex items-center gap-2"><Award className="w-4 h-4" /> 4. Award Decision (you choose)</span>
                  <button
                    onClick={async () => {
                      const linesArr = vendorQuoteCmp.items.map(row => {
                        const sid = awardChoices[row.rfq_item_id];
                        if (!sid) return null;
                        const cell = row.cells.find(c => c.supplier_id === sid && c.covered);
                        if (!cell) return null;
                        return {
                          rfq_item_id:   row.rfq_item_id,
                          supplier_id:   sid,
                          quotation_id:  cell.quote_id,
                          awarded_quantity: row.quantity,
                          unit_price:    cell.unit_price,
                          reason:        'workspace award (vendor quote)',
                        };
                      }).filter(Boolean);
                      if (linesArr.length === 0) { toast.error('Pick a supplier for ≥1 item'); return; }
                      setBusy(true);
                      try {
                        const r = await axiosInstance.post(`/rfqs/${id}/awards`, { lines: linesArr });
                        toast.success(`✓ ${r.data?.awarded_count || 0} line(s) awarded`);
                        refresh();
                      } catch (e) {
                        const d = e?.response?.data?.detail;
                        toast.error((d && (d.error || d.message)) || 'Award failed');
                      } finally { setBusy(false); }
                    }}
                    disabled={busy || Object.keys(awardChoices).length === 0}
                    data-testid="rfq-vqc-approve-awards"
                    className="px-3 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-50">
                    ✓ Approve Awards ({Object.keys(awardChoices).length})
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-left px-3 py-2">Award To</th>
                      <th className="text-left px-3 py-2">Price</th>
                      <th className="text-left px-3 py-2">Lead</th>
                      <th className="text-left px-3 py-2">Terms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorQuoteCmp.items.map(row => {
                      const sid = awardChoices[row.rfq_item_id];
                      const cell = sid ? row.cells.find(c => c.supplier_id === sid && c.covered) : null;
                      return (
                        <tr key={row.rfq_item_id} className="border-t border-gray-100"
                            data-testid={`rfq-vqc-award-row-${row.item_code || row.rfq_item_id}`}>
                          <td className="px-3 py-2 text-sm">{row.description}</td>
                          <td className="px-3 py-2">
                            <select value={sid || ''}
                                     onChange={(e) => setAwardChoices(p => ({...p, [row.rfq_item_id]: e.target.value || undefined}))}
                                     data-testid={`rfq-vqc-award-select-${row.item_code || row.rfq_item_id}`}
                                     className="text-xs border border-gray-300 rounded px-2 py-1">
                              <option value="">— pick supplier —</option>
                              {row.cells.filter(c => c.covered).map(c => (
                                <option key={c.supplier_id} value={c.supplier_id}>
                                  {c.supplier_name} · {c.unit_price} {c.currency}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{cell?.unit_price?.toLocaleString() || '—'}</td>
                          <td className="px-3 py-2 text-xs">{cell?.lead_time_days != null ? `${cell.lead_time_days}d` : '—'}</td>
                          <td className="px-3 py-2 text-xs">{cell?.payment_terms || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Recommendation Lock + Execution Bridge */}
      {(matrix?.items?.length > 0 || vendorQuoteCmp?.items?.length > 0) && (
        <div className="border border-violet-300 bg-violet-50 rounded-lg p-3 flex items-center justify-between gap-2"
             data-testid="rfq-execution-bridge">
          <div className="text-sm flex items-center gap-3 flex-wrap">
            <span className="font-semibold inline-flex items-center gap-1">
              <Lock className="w-4 h-4" /> Execution Safety
            </span>
            {header.decision_lock_hash ? (
              <span className="text-xs px-2 py-0.5 rounded bg-violet-200 text-violet-900 font-mono"
                    data-testid="rfq-lock-chip"
                    title={header.decision_lock_hash}>
                Locked · {header.decision_lock_hash.slice(0, 12)}…
              </span>
            ) : (
              <span className="text-xs text-violet-700">Recommendation not locked</span>
            )}
            {spawnedPOs.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 inline-flex items-center gap-1"
                    data-testid="rfq-po-count-chip">
                <Truck className="w-3 h-3" /> {spawnedPOs.length} PO{spawnedPOs.length === 1 ? '' : 's'} generated
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {!header.decision_lock_hash && (
              <button onClick={lockRecommendation} disabled={busy || !rec}
                      data-testid="rfq-lock-recommendation-button"
                      className="px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                <Lock className="w-4 h-4" /> Lock Recommendation
              </button>
            )}
            {isAwarded && spawnedPOs.length === 0 && header.decision_lock_hash && (
              <button onClick={generatePOs} disabled={busy}
                      data-testid="rfq-generate-pos-button"
                      className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                <FileText className="w-4 h-4" /> Generate POs from Locked Awards
              </button>
            )}
          </div>
        </div>
      )}

      {/* Spawned POs panel */}
      {spawnedPOs.length > 0 && (
        <div className="border border-indigo-200 bg-indigo-50 rounded-lg overflow-hidden"
             data-testid="rfq-spawned-pos">
          <div className="px-3 py-2 bg-indigo-100 border-b border-indigo-300 font-semibold text-sm text-indigo-900 flex items-center gap-2">
            <Truck className="w-4 h-4" /> Purchase Orders Generated from This RFQ
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-indigo-900 bg-indigo-50">
              <tr>
                <th className="text-left px-3 py-1">PO No.</th>
                <th className="text-left px-3 py-1">Supplier</th>
                <th className="text-right px-3 py-1">Lines</th>
                <th className="text-right px-3 py-1">Total</th>
                <th className="text-left px-3 py-1">Receipt</th>
                <th className="text-left px-3 py-1">Chain Hash</th>
                <th className="text-left px-3 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {spawnedPOs.map((p) => (
                <tr key={p.id} className="border-t border-indigo-200"
                    data-testid={`rfq-po-row-${p.po_number}`}>
                  <td className="px-3 py-1 font-mono text-xs">{p.po_number}</td>
                  <td className="px-3 py-1 text-xs">{p.supplier_name}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{(p.items || []).length}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{p.total?.toLocaleString()}</td>
                  <td className="px-3 py-1 text-xs">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      p.received_status === 'fully_received' ? 'bg-emerald-100 text-emerald-800' :
                      p.received_status === 'partial' ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {(p.received_status || 'not_received').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-1 font-mono text-[10px] text-violet-700"
                      title={p.execution_chain_hash}>
                    {p.execution_chain_hash?.slice(0, 10)}…
                  </td>
                  <td className="px-3 py-1 text-right">
                    <button onClick={() => navigate(`/purchase-orders/${p.id}/receive`)}
                            data-testid={`rfq-po-open-${p.po_number}`}
                            className="text-xs text-indigo-700 hover:text-indigo-900 underline">
                      Open GRN
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Awarded summary */}
      {awards.length > 0 && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg overflow-hidden"
             data-testid="rfq-awards-summary">
          <div className="px-3 py-2 bg-emerald-100 border-b border-emerald-300 font-semibold text-sm text-emerald-900">
            Awards
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-emerald-900 bg-emerald-50">
              <tr>
                <th className="text-left px-3 py-1">Item</th>
                <th className="text-left px-3 py-1">Supplier</th>
                <th className="text-right px-3 py-1">Qty</th>
                <th className="text-right px-3 py-1">Unit</th>
                <th className="text-right px-3 py-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {awards.map((a) => (
                <tr key={a.id} className="border-t border-emerald-200"
                    data-testid={`award-row-${a.rfq_item_id}`}>
                  <td className="px-3 py-1 text-xs">{a.description}</td>
                  <td className="px-3 py-1 text-xs">{a.supplier_name} <span className="text-[10px] font-mono text-emerald-700">V{a.revision_number}</span></td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{a.awarded_quantity}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{a.unit_price}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{a.total?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


export default RFQWorkspace;
